import React, { useState } from 'react';
import {
    salesPdfSections, salesPeriodFlags, salesEnhancedExportQuery, salesReportFigures, buildSalesPdfData, fixedExpenseRows,
} from '../utils/salesReportPdfData';
import { downloadEditorialSalesPdf } from '../utils/salesReportEditorialPdf';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Check } from 'lucide-react';
import http from '../services/httpClient';
import { getPreparedFor } from '../utils/businessIdentity';


const ExportReportModal = ({
    isOpen,
    onClose,
    chartData = [],
    topProfitItems = [],
    summary = {},
    summaryComparison,
    dailyBreakdown = [],
    expenses = {},
    wastageExpenses = {},
    selectedPeriod = '',
    displayText = '',
    selectedWeek,
    selectedDate,
    fixedMonthlyExpense,
    outletBreakdown = { rows: [] },
    fixedShare = null,
}) => {
    // The sections this period offers — the same list Share renders.
    const { isCustomMultiDayRange } = salesPeriodFlags({ selectedPeriod, selectedDate, selectedWeek });
    const SECTIONS = salesPdfSections({ selectedPeriod, selectedDate, selectedWeek, topProfitItems, dailyBreakdown, outletRows: outletBreakdown.rows, fixedShare });
    const fixedRows = fixedExpenseRows(fixedShare);

    const [selectedSections, setSelectedSections] = useState(SECTIONS.map(s => s.id));
    const [exportFormat, setExportFormat] = useState('pdf');
    const [isExporting, setIsExporting] = useState(false);
    const [exportDone, setExportDone] = useState(false);

    const [enhancedData, setEnhancedData] = useState(null);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [fetchError, setFetchError] = useState(false);

    React.useEffect(() => {
        if (isOpen) {
            setSelectedSections(SECTIONS.map(s => s.id));
            setExportDone(false);
            setEnhancedData(null);
            setFetchError(false);

            // The period query uses the SAME rules SalesReport uses for its own /summary
            // fetch, so both endpoints resolve the exact same period boundaries.
            const fetchData = async () => {
                setIsLoadingData(true);
                try {
                    const queryStr = salesEnhancedExportQuery({
                        selectedPeriod, selectedDate, selectedWeek,
                        businessId: localStorage.getItem('businessId') || '',
                        language: localStorage.getItem('selectedLanguage') || 'en',
                    });
                    const response = await http.get(`/reports/enhanced-export${queryStr}`);
                    if (response && response.success) {
                        setEnhancedData(response.data);
                    } else {
                        setFetchError(true);
                    }
                } catch (err) {
                    console.error('Error fetching enhanced report data:', err);
                    setFetchError(true);
                } finally {
                    setIsLoadingData(false);
                }
            };
            fetchData();
        }
    }, [isOpen, selectedWeek, selectedDate, selectedPeriod]);

    // Which sections depend on the async /enhanced-export payload (vs. props).
    const ENHANCED_SECTION_KEY = {
        'weekly-monthly-yearly-summary': 'summary',
        'items-sold-breakdown': 'items_sold',
        'stock-in-details': 'stock_in',
        'stock-out-details': 'stock_out',
        'non-chargeable-expenses': 'non_chargeable',
    };
    // A section has data unless it is enhanced-backed and its payload is missing/empty.
    // While loading we treat it as available (unknown yet).
    const sectionHasData = (id) => {
        // The Weekly/Monthly/Yearly comparison is anchored to the calendar week/month/
        // year around the selection; for an arbitrary custom range it is misleading
        // (e.g. the Yearly and Selected-Period rows collapse to the same figure), so it
        // is not offered in Range mode — the export then covers only the chosen range.
        if (id === 'weekly-monthly-yearly-summary' && isCustomMultiDayRange) return false;
        const key = ENHANCED_SECTION_KEY[id];
        if (!key) return true;
        if (isLoadingData) return true;
        const val = enhancedData ? enhancedData[key] : null;
        if (!val) return false;
        return Array.isArray(val) ? val.length > 0 : true;
    };

    // Once the fetch settles, drop unavailable enhanced sections from the selection so
    // the button count and the export never claim to include empty/failed sections.
    React.useEffect(() => {
        if (isLoadingData) return;
        setSelectedSections(prev => prev.filter(id => sectionHasData(id)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enhancedData, isLoadingData]);


    if (!isOpen) return null;

    const toggleSection = (id) => {
        setSelectedSections(prev =>
            prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
        );
    };

    // Only sections that actually have data can be selected (unavailable ones are skipped).
    const availableSectionIds = SECTIONS.map(s => s.id).filter(id => sectionHasData(id));
    const allAvailableSelected = availableSectionIds.length > 0 && availableSectionIds.every(id => selectedSections.includes(id));

    const toggleAll = () => {
        setSelectedSections(allAvailableSelected ? [] : availableSectionIds);
    };

    const periodLabel = displayText || selectedPeriod || 'Report';
    const fileTimestamp = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

    // ─── Financial truth: mirrors the on-screen SalesReport cards exactly ───
    const {
        cogsUnavailable, excludedCogsCount, excludedCogsRevenue, excludedCogsNames, excludedFootnote,
        cogsEstimated, estimatedCogsAmount, estimatedCogsCount, estimatedFootnote,
        headlineProfit, headlineLabel, displayCogs, split, profitBasis,
    } = salesReportFigures(summary);
    // Parse a 'YYYY-MM-DD' string as a LOCAL date (avoids the UTC-midnight off-by-one).
    const parseLocalDate = (s) => {
        const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
        return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(s);
    };

    // ═══════════════════════════════════════════════════════
    // ─── PDF Export ───────────────────────────────────────
    // ═══════════════════════════════════════════════════════
    const exportPDF = async () => {
        setIsExporting(true);
        try {
            const preparedFor = await getPreparedFor();
            const generated = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
            const data = buildSalesPdfData({
                summary, expenses, wastageExpenses, topProfitItems, chartData, dailyBreakdown,
                selectedPeriod, selectedDate, selectedWeek, displayText,
                enhanced: enhancedData, preparedFor, generated,
                outletRows: outletBreakdown.rows,
                fixedShare,
            });

            await downloadEditorialSalesPdf(data, selectedSections, `Sales_Report_${fileTimestamp}.pdf`);
            setExportDone(true);
        } catch (err) {
            console.error('PDF export failed:', err);
            alert('PDF export failed: ' + ((err && err.message) ? err.message : 'unknown error') + '\nPlease try again.');
        } finally {
            setIsExporting(false);
        }
    };

    // ═══════════════════════════════════════════════════════
    // ─── Excel Export (proper .xlsx with styling) ─────────
    // ═══════════════════════════════════════════════════════
    const exportExcel = () => {
        setIsExporting(true);
        try {
            const wb = XLSX.utils.book_new();
            // Set workbook properties for better compatibility
            wb.Props = {
                Title: 'Sales Report',
                Subject: `Period: ${periodLabel}`,
                Author: 'Invexis',
                CreatedDate: new Date(),
            };

            // NOTE: this file imports the plain `xlsx` community build, which does NOT
            // serialize cell styles (fills/fonts via the `s` property) — only number
            // formats (`z`), column widths and merges. A navy/bold header block was
            // therefore a no-op and has been removed. If styled headers are ever needed,
            // switch to the `xlsx-js-style` build and assign `ws[addr].s`.

            // Currency number format — "Rs." + fixed 2 decimals, matching the PDF's fmt()
            // so the same figure reads identically in both outputs.
            const applyCurrencyFormat = (ws, colIndices, startRow, endRow) => {
                for (let r = startRow; r <= endRow; r++) {
                    colIndices.forEach(c => {
                        const addr = XLSX.utils.encode_cell({ r, c });
                        if (ws[addr] && typeof ws[addr].v === 'number') {
                            ws[addr].z = '"Rs."#,##0.00';
                        }
                    });
                }
            };

            // ── 1) Total Sales sheet — shows the 3 card values: Total Sales, Expenses, Gross Profit
            if (selectedSections.includes('total-sales')) {
                // The Expense card's value: Stock-In + wastage (fixed expenses not added, for now).
                const totalExpenseVal = Number(expenses.total_expenses || 0) + Number(wastageExpenses.total_expenses || 0);
                const wsData = [
                    ['Sales Report - Total Sales'],
                    [`Period: ${periodLabel}`, '', `Generated: ${new Date().toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}`],
                    [],
                    ['Metric', 'Value'],
                    ['Total Sales', Number(summary.total_sales || 0)],
                    ['Total Expenses', totalExpenseVal],
                    // Match the on-screen headline card (net-of-wastage; "-" when COGS unknown).
                    [headlineLabel, cogsUnavailable ? '-' : Number(headlineProfit)],
                ];
                // Keep the number numeric (so the sheet still sums) and put the mark in
                // a footnote row instead of prefixing "~" onto the cell value.
                if (estimatedFootnote) wsData.push([], [estimatedFootnote]);
                if (excludedFootnote) wsData.push([], [excludedFootnote]);
                const ws = XLSX.utils.aoa_to_sheet(wsData);
                ws['!cols'] = [{ wch: 22 }, { wch: 18 }];
                ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }];
                applyCurrencyFormat(ws, [1], 4, 6);
                XLSX.utils.book_append_sheet(wb, ws, 'Total Sales');
            }

            // ── 2) Expenses sheet — breakdown of expense card value
            if (selectedSections.includes('expenses')) {
                const stockInExp = Number(expenses.total_expenses || 0);
                const wastageExp = Number(wastageExpenses.total_expenses || 0);
                const totalExpense = stockInExp + wastageExp;
                const wsData = [
                    ['Sales Report - Expenses'],
                    [`Period: ${periodLabel}`, '', `Generated: ${new Date().toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}`],
                    [],
                    ['Category', 'Amount (Rs.)'],
                    ['Stock-In Expenses', stockInExp],
                    ['Wastage / Stock-Out Expenses', wastageExp],
                    [],
                    ['Total Expenses', totalExpense],
                ];
                const ws = XLSX.utils.aoa_to_sheet(wsData);
                ws['!cols'] = [{ wch: 30 }, { wch: 18 }];
                ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }];
                applyCurrencyFormat(ws, [1], 4, wsData.length - 1);
                XLSX.utils.book_append_sheet(wb, ws, 'Expenses');
            }

            // ── 3) Gross Profit sheet — breakdown of gross profit card value
            if (selectedSections.includes('gross-profit')) {
                const totalSales = Number(summary.total_sales || 0);
                const grossProfit = split.profit;            // the card's figure: net of wastage
                const totalWastage = Number(summary.total_wastage || 0);
                const cogs = displayCogs;                     // adjusted/display COGS the pie uses
                const gpVal = cogsUnavailable ? '-' : grossProfit;
                const cogsVal = cogsUnavailable ? '-' : cogs;
                const gpPct = cogsUnavailable ? '-' : (profitBasis > 0 ? `${((grossProfit / profitBasis) * 100).toFixed(1)}%` : '0%');
                const cogsPct = cogsUnavailable ? '-' : (profitBasis > 0 ? `${((cogs / profitBasis) * 100).toFixed(1)}%` : '0%');

                const wsData = [
                    ['Sales Report - Gross Profit'],
                    [`Period: ${periodLabel}`, '', `Generated: ${new Date().toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}`],
                    [],
                    ['Component', 'Amount (Rs.)', '% of Net Sale'],
                    // Net of wastage, matching the Gross Profit card and the on-screen
                    // donut. The sheet used to export the figure BEFORE wastage while also
                    // listing Waste & Losses underneath it, so the three rows summed past
                    // Net Sale by exactly the wastage.
                    ['Gross Profit (after wastage)', gpVal, gpPct],
                    ['Manufacturing Cost (COGS)', cogsVal, cogsPct],
                    // Share of the SAME basis as the two rows above, so the three total 100%.
                    ['Waste & Losses', totalWastage, cogsUnavailable ? '-' : (profitBasis > 0 ? `${((totalWastage / profitBasis) * 100).toFixed(1)}%` : '0%')],
                    [],
                    // The two percentages above are shares of the COSTED sales, so that is
                    // the row carrying 100%. Net Sale is larger whenever part of the bill
                    // could not be costed, and saying so is the difference between a
                    // reconcilable sheet and one that appears not to add up.
                    ...(profitBasis < totalSales - 0.01
                        ? [['Costed Sales (basis for %)', profitBasis, '100%'], ['Net Sale (Total)', totalSales, '']]
                        : [['Net Sale (Total)', totalSales, '100%']]),
                ];
                const currencyEndRow = wsData.length - 1;
                if (estimatedFootnote) wsData.push([], [estimatedFootnote]);
                if (excludedFootnote) wsData.push([], [excludedFootnote]);
                const ws = XLSX.utils.aoa_to_sheet(wsData);
                ws['!cols'] = [{ wch: 28 }, { wch: 18 }, { wch: 16 }];
                ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 2 } }];
                // Bounded to the numeric rows so the footnote is not currency-formatted.
                applyCurrencyFormat(ws, [1], 4, currencyEndRow);
                XLSX.utils.book_append_sheet(wb, ws, 'Gross Profit');
            }

            // ── 5) Sales Trend sheet
            if (selectedSections.includes('sales-trend') && chartData.length > 0) {
                const wsData = [
                    ['Sales Report - Sales Trend'],
                    [`Period: ${periodLabel}`],
                    [],
                    ['Period', 'Total Sales (Rs.)', 'Gross Profit (Rs.)'],
                    ...chartData.map(d => [d.label || '', Number(d.total_sales || 0), Number(d.gross_profit || 0)]),
                    [],
                    ['Total', chartData.reduce((s, d) => s + Number(d.total_sales || 0), 0), chartData.reduce((s, d) => s + Number(d.gross_profit || 0), 0)],
                ];
                const ws = XLSX.utils.aoa_to_sheet(wsData);
                ws['!cols'] = [{ wch: 18 }, { wch: 18 }, { wch: 18 }];
                ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 2 } }];
                applyCurrencyFormat(ws, [1, 2], 4, wsData.length - 1);
                XLSX.utils.book_append_sheet(wb, ws, 'Sales Trend');
            }

            // ── 6) Date-wise Breakdown
            if (selectedSections.includes('date-breakdown') && dailyBreakdown.length > 0) {
                const wsData = [
                    ['Sales Report - Date-wise Breakdown'],
                    [`Period: ${periodLabel}`],
                    [],
                    ['Date', 'Total Sales (Rs.)', 'Expense (Rs.)', 'Wastage (Rs.)'],
                    ...dailyBreakdown.map(row => {
                        const d = parseLocalDate(row.date);
                        return [
                            `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`,
                            Number(row.total_sales || 0),
                            Number(row.expense || 0),
                            Number(row.wastage || 0),
                        ];
                    }),
                    [],
                    ['TOTAL',
                        dailyBreakdown.reduce((s, r) => s + (r.total_sales || 0), 0),
                        dailyBreakdown.reduce((s, r) => s + (r.expense || 0), 0),
                        dailyBreakdown.reduce((s, r) => s + (r.wastage || 0), 0),
                    ],
                ];
                const ws = XLSX.utils.aoa_to_sheet(wsData);
                ws['!cols'] = [{ wch: 16 }, { wch: 18 }, { wch: 18 }, { wch: 18 }];
                ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 3 } }];
                applyCurrencyFormat(ws, [1, 2, 3], 4, wsData.length - 1);
                XLSX.utils.book_append_sheet(wb, ws, 'Date Breakdown');
            }

            // ── Outlet-wise Sales (Outlet Sale) ──
            if (selectedSections.includes('outlet-sales') && (outletBreakdown.rows || []).some((r) => r.outlet)) {
                const rows = outletBreakdown.rows;
                const ws = XLSX.utils.aoa_to_sheet([
                    ['Sales Report - Outlet-wise Sales'],
                    [`Period: ${periodLabel}`],
                    [],
                    ['Outlet', 'Days', 'Sales', 'Discount', 'Gross Profit'],
                    ...rows.map((r) => [r.label, r.days, r.sales, r.discount, r.gross_profit == null ? '' : r.gross_profit]),
                    ['TOTAL', '', rows.reduce((t, r) => t + (Number(r.sales) || 0), 0), rows.reduce((t, r) => t + (Number(r.discount) || 0), 0), ''],
                    ...(rows.some((r) => r.gross_profit != null && r.gross_profit_estimated)
                        ? [[], ['Gross Profit for some outlets includes an estimated cost for Not-in-Menu revenue, at the food-cost ratio of the costed dishes.']]
                        : []),
                ]);
                XLSX.utils.book_append_sheet(wb, ws, 'Outlets');
            }

            // ── 7) Top Items
            if (selectedSections.includes('top-items') && topProfitItems.length > 0) {
                // Real gross profit (losses shown, not clamped to 0); share dashed when total ≤ 0.
                const totalGP = topProfitItems.reduce((s, i) => s + Number(i.gross_profit || 0), 0);
                const wsData = [
                    ['Sales Report - Top Items by Gross Profit'],
                    [`Period: ${periodLabel}`],
                    [],
                    ['Rank', 'Item Name', 'Gross Profit (Rs.)', 'Share %'],
                    ...topProfitItems.map((item, i) => {
                        const gp = Number(item.gross_profit || 0);
                        return [i + 1, item.item_name || '', gp, totalGP > 0 ? `${((gp/totalGP)*100).toFixed(1)}%` : '-'];
                    }),
                    [],
                    ['', 'Total', totalGP, '100%'],
                ];
                const ws = XLSX.utils.aoa_to_sheet(wsData);
                ws['!cols'] = [{ wch: 6 }, { wch: 28 }, { wch: 18 }, { wch: 12 }];
                ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 3 } }];
                applyCurrencyFormat(ws, [2], 4, wsData.length - 1);
                XLSX.utils.book_append_sheet(wb, ws, 'Top Items');
            }

            // ── 8) Profit vs COGS
            if (selectedSections.includes('profit-cogs')) {
                const totalSales = Number(summary.total_sales || 0);
                const grossProfit = split.profit;            // the card's figure: net of wastage
                const totalWastage = Number(summary.total_wastage || 0);
                const cogs = displayCogs;                     // adjusted/display COGS the pie uses
                const gpPct = cogsUnavailable ? '-' : (profitBasis > 0 ? `${((grossProfit/profitBasis)*100).toFixed(1)}%` : '0%');
                const cogsPct = cogsUnavailable ? '-' : (profitBasis > 0 ? `${((cogs/profitBasis)*100).toFixed(1)}%` : '0%');

                const wsData = [
                    ['Sales Report - Profit vs Manufacturing Cost'],
                    [`Period: ${periodLabel}`],
                    [],
                    ['Component', 'Amount (Rs.)', '% of Net Sale'],
                    ['Gross Profit (after wastage)', cogsUnavailable ? '-' : grossProfit, gpPct],
                    ['Manufacturing Cost (COGS)', cogsUnavailable ? '-' : cogs, cogsPct],
                    ['Waste & Losses', totalWastage, cogsUnavailable ? '-' : (profitBasis > 0 ? `${((totalWastage / profitBasis) * 100).toFixed(1)}%` : '0%')],
                    [],
                    // Same reconciliation as the Gross Profit sheet: the percentages are
                    // shares of the costed sales, so that row is the 100%.
                    ...(profitBasis < totalSales - 0.01
                        ? [['Costed Sales (basis for %)', profitBasis, '100%'], ['Net Sale (Total)', totalSales, '']]
                        : [['Net Sale (Total)', totalSales, '100%']]),
                ];
                const currencyEndRow = wsData.length - 1;
                if (estimatedFootnote) wsData.push([], [estimatedFootnote]);
                if (excludedFootnote) wsData.push([], [excludedFootnote]);
                const ws = XLSX.utils.aoa_to_sheet(wsData);
                ws['!cols'] = [{ wch: 28 }, { wch: 18 }, { wch: 16 }];
                ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 2 } }];
                applyCurrencyFormat(ws, [1], 4, currencyEndRow);
                XLSX.utils.book_append_sheet(wb, ws, 'Profit vs COGS');
            }

            // ── 9) Weekly, Monthly, Yearly Summary sheet
            if (selectedSections.includes('weekly-monthly-yearly-summary') && enhancedData?.summary) {
                const summ = enhancedData.summary;
                const wsData = [
                    ['Sales & Expense Period Scope Summary'],
                    [],
                    ['Period Scope', 'Sales Revenue (Rs.)', 'Total Expenses (Rs.)', 'Net Profit/Loss (Rs.)'],
                    ['Weekly Period', Number(summ.weekly?.sales || 0), Number(summ.weekly?.expenses || 0), Number(summ.weekly?.profit || 0)],
                    ['Monthly Period', Number(summ.monthly?.sales || 0), Number(summ.monthly?.expenses || 0), Number(summ.monthly?.profit || 0)],
                    ['Yearly Period', Number(summ.yearly?.sales || 0), Number(summ.yearly?.expenses || 0), Number(summ.yearly?.profit || 0)],
                    ['Selected Period (Primary)', Number(summ.primary?.sales || 0), Number(summ.primary?.expenses || 0), Number(summ.primary?.profit || 0)]
                ];
                const ws = XLSX.utils.aoa_to_sheet(wsData);
                ws['!cols'] = [{ wch: 28 }, { wch: 18 }, { wch: 18 }, { wch: 20 }];
                ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 3 } }];
                applyCurrencyFormat(ws, [1, 2, 3], 3, 6);
                XLSX.utils.book_append_sheet(wb, ws, 'Summary Breakdown');
            }

            // ── 10) Items Sold Detailed Breakdown sheet
            if (selectedSections.includes('items-sold-breakdown') && enhancedData?.items_sold?.length > 0) {
                const wsData = [
                    ['Detailed Items Sold Breakdown'],
                    [],
                    ['Item Name', 'Quantity Sold', 'Total Sales Amount (Rs.)', 'Orders Count', 'Contribution %'],
                    ...enhancedData.items_sold.map(item => [
                        item.item_name,
                        Number(item.quantity_sold || 0),
                        Number(item.total_sales_amount || 0),
                        Number(item.number_of_orders || 0),
                        `${item.contribution_percentage}%`
                    ])
                ];
                const ws = XLSX.utils.aoa_to_sheet(wsData);
                ws['!cols'] = [{ wch: 30 }, { wch: 14 }, { wch: 22 }, { wch: 14 }, { wch: 16 }];
                ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }];
                applyCurrencyFormat(ws, [2], 3, wsData.length - 1);
                XLSX.utils.book_append_sheet(wb, ws, 'Sales Details');
            }

            // ── 11) Stock In Details sheet
            if (selectedSections.includes('stock-in-details') && enhancedData?.stock_in?.length > 0) {
                const wsData = [
                    ['Stock-In Details (Ingredient Purchases)'],
                    [],
                    ['Ingredient Name', 'Quantity Added', 'Unit', 'Purchase Cost (Rs.)', 'Supplier', 'Date'],
                    ...enhancedData.stock_in.map(item => [
                        item.ingredient_name,
                        Number(item.quantity_added || 0),
                        item.unit,
                        Number(item.purchase_cost || 0),
                        item.supplier,
                        item.date
                    ])
                ];
                const ws = XLSX.utils.aoa_to_sheet(wsData);
                ws['!cols'] = [{ wch: 28 }, { wch: 16 }, { wch: 10 }, { wch: 18 }, { wch: 22 }, { wch: 14 }];
                ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }];
                applyCurrencyFormat(ws, [3], 3, wsData.length - 1);
                XLSX.utils.book_append_sheet(wb, ws, 'Stock In Details');
            }

            // ── 12) Stock Out Details sheet
            if (selectedSections.includes('stock-out-details') && enhancedData?.stock_out?.length > 0) {
                const wsData = [
                    ['Stock-Out Details (Ingredient Usage)'],
                    [],
                    ['Ingredient Name', 'Quantity Consumed', 'Unit', 'Related Item/Recipe', 'Date'],
                    ...enhancedData.stock_out.map(item => [
                        item.ingredient_name,
                        Number(item.quantity_consumed || 0),
                        item.unit,
                        item.related_item,
                        item.date
                    ])
                ];
                const ws = XLSX.utils.aoa_to_sheet(wsData);
                ws['!cols'] = [{ wch: 28 }, { wch: 18 }, { wch: 10 }, { wch: 32 }, { wch: 14 }];
                ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }];
                XLSX.utils.book_append_sheet(wb, ws, 'Stock Out Details');
            }

            // ── 13) Non-Chargeable Expenses sheet
            if (selectedSections.includes('non-chargeable-expenses') && enhancedData?.non_chargeable?.length > 0) {
                const wsData = [
                    ['Non-Chargeable Expenses (NC)'],
                    [],
                    ['Expense Name', 'Category', 'Amount (Rs.)', 'Date', 'Remarks'],
                    ...enhancedData.non_chargeable.map(item => [
                        item.expense_name,
                        item.category,
                        Number(item.amount || 0),
                        item.date,
                        item.remarks
                    ])
                ];
                const ws = XLSX.utils.aoa_to_sheet(wsData);
                ws['!cols'] = [{ wch: 28 }, { wch: 18 }, { wch: 16 }, { wch: 14 }, { wch: 32 }];
                ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }];
                applyCurrencyFormat(ws, [2], 3, wsData.length - 1);
                XLSX.utils.book_append_sheet(wb, ws, 'Non-Chargeable Expenses');
            }

            // ── 14) Fixed Expenses sheet — the Fixed Expense tab, same groups and masking
            if (selectedSections.includes('fixed-expenses') && fixedRows) {
                const wsData = [
                    ['Fixed Expenses'],
                    [`Period: ${periodLabel}`],
                    [],
                    ['Expense', 'Group', 'Type', 'Status', 'Payment', 'Share of Period (Rs.)'],
                    ...fixedRows.groups.flatMap((g) => g.rows.map((r) => [r.name, g.name, r.behavior, r.status, r.payment, r.amount == null ? 'Hidden' : r.amount])),
                    [],
                    [`Total${fixedRows.excludesSalaries ? ' (excl. staff salaries)' : ''}${fixedRows.estimated ? ' — est.' : ''}`, '', '', '', '', fixedRows.total],
                    [`Paid ${fixedRows.paid} · Due ${fixedRows.due}`],
                ];
                const ws = XLSX.utils.aoa_to_sheet(wsData);
                ws['!cols'] = [{ wch: 30 }, { wch: 24 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 20 }];
                ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }];
                applyCurrencyFormat(ws, [5], 4, wsData.length - 2);
                XLSX.utils.book_append_sheet(wb, ws, 'Fixed Expenses');
            }

            if (wb.SheetNames.length === 0) {
                alert('No data to export. Please select at least one section.');
                setIsExporting(false);
                return;
            }

            // Write with proper MIME type for Excel
            const wbout = XLSX.write(wb, {
                bookType: 'xlsx',
                type: 'array',
                bookSST: true,      // shared string table for smaller file size
                compression: true,  // compress for smaller file
            });
            saveAs(
                new Blob([wbout], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                }),
                `Sales_Report_${fileTimestamp}.xlsx`
            );
            setExportDone(true);
        } catch (err) {
            console.error('Excel export failed:', err);
            alert('Excel export failed. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    const exportJSON = () => {
        setIsExporting(true);
        try {
            const exportData = {
                period: periodLabel,
                sections: selectedSections,
                data: {
                    summary,
                    summaryComparison,
                    dailyBreakdown,
                    chartData,
                    topProfitItems,
                    expenses,
                    wastageExpenses,
                    fixedMonthlyExpense
                }
            };
            const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
            saveAs(blob, `Sales_Report_${fileTimestamp}.json`);
            setExportDone(true);
        } catch (err) {
            console.error('JSON export failed:', err);
            alert('JSON export failed. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    const handleExport = () => {
        if (selectedSections.length === 0) {
            alert('Please select at least one section to export.');
            return;
        }
        if (exportFormat === 'pdf') exportPDF();
        else if (exportFormat === 'json') exportJSON();
        else exportExcel();
    };

    // ─── Success View ──────────────────────────────────────
    if (exportDone) {
        return (
            <div style={overlayStyle} onClick={() => { setExportDone(false); onClose(); }}>
                <div style={{ ...modalStyle, width: 'min(380px, 90vw)', padding: '16px 20px' }} onClick={e => e.stopPropagation()}>
                    <div style={{ textAlign: 'center', padding: '8px 0' }}>
                        <div style={{ width: 60, height: 60, borderRadius: '50%', border: '3px solid #22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                            <Check size={32} color="#22c55e" strokeWidth={3} />
                        </div>
                        <h2 style={{ fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 10 }}>Export Complete!</h2>
                        <p style={{ color: '#4b5563', fontSize: 15, marginBottom: 20, lineHeight: 1.5 }}>
                            Your {exportFormat === 'pdf' ? 'PDF' : 'Excel (.xlsx)'} has successfully downloaded.
                        </p>
                        <button onClick={() => { setExportDone(false); onClose(); }} style={primaryBtnStyle}>OK</button>
                    </div>
                </div>
            </div>
        );
    }

    // ─── Group label helper ────────────────────────────────
    const summaryIds = ['total-sales', 'expenses', 'gross-profit', 'weekly-monthly-yearly-summary'];
    const chartIds = SECTIONS.filter(s => s.group !== 'summary').map(s => s.id);

    // Close is blocked while an export is running so the backdrop/× can't interrupt the
    // PDF's live-DOM reparent/restore and leave the on-screen report broken.
    const requestClose = () => { if (!isExporting) onClose(); };

    // ─── Main Modal ──────────────────────────────────────
    return (
        <div style={overlayStyle} onClick={requestClose}>
            <div style={modalStyle} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#1f2937' }}>Export Report</h3>
                    <button onClick={requestClose} disabled={isExporting} style={{ background: 'none', border: 'none', fontSize: 24, cursor: isExporting ? 'not-allowed' : 'pointer', color: '#9ca3af', lineHeight: 1, opacity: isExporting ? 0.5 : 1 }}>&times;</button>
                </div>

                {/* Format Toggle */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                    {[
                        { key: 'pdf', label: 'PDF', desc: 'Charts + Tables' },
                        { key: 'excel', label: 'Excel', desc: 'Data Sheets' },
                    ].map(f => (
                        <button
                            key={f.key}
                            onClick={() => setExportFormat(f.key)}
                            style={{
                                flex: 1, padding: '12px 8px', borderRadius: '0.5rem',
                                border: `1.5px solid ${exportFormat === f.key ? '#9ca3af' : '#d1d5db'}`,
                                background: exportFormat === f.key ? '#f3f4f6' : '#fff',
                                cursor: 'pointer', transition: 'all 0.2s', textAlign: 'center',
                            }}
                        >
                            <div style={{ fontSize: 15, fontWeight: 700, color: exportFormat === f.key ? '#111827' : '#374151' }}>{f.label}</div>
                            <div style={{ fontSize: 11, color: exportFormat === f.key ? '#6b7280' : '#9ca3af', marginTop: 2 }}>{f.desc}</div>
                        </button>
                    ))}
                </div>

                {/* Detailed-data fetch failure — surfaced instead of silently dropping sections */}
                {fetchError && !isLoadingData && (
                    <div style={{ padding: '10px 12px', marginBottom: 14, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.5rem', fontSize: 12, color: '#b91c1c', lineHeight: 1.4 }}>
                        Detailed report data (items sold, stock-in/out, non-chargeable, weekly/monthly/yearly summary) could not be loaded. Those sections are unavailable for this export.
                    </div>
                )}

                {/* Data-quality warning — mirrors the on-screen "COGS unavailable / estimated" notice */}
                {(cogsUnavailable || summary.has_estimated_items) && (
                    <div style={{ padding: '10px 12px', marginBottom: 14, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '0.5rem', fontSize: 12, color: '#92400e', lineHeight: 1.4 }}>
                        {cogsUnavailable
                            ? 'Some sold items have unavailable cost data, so Gross Profit / Manufacturing Cost are shown as "-" in this export (matching the on-screen report).'
                            : `${summary.estimated_item_count || ''} item(s) were sold without recipe/ingredients; their cost is estimated.`}
                    </div>
                )}

                {/* Estimated-cost notice. Shown BEFORE the export runs so it is a decision
                    point, not a discovery made after the file has already been sent on. */}
                {cogsEstimated && (
                    <div style={{ padding: '10px 12px', marginBottom: 14, background: '#fefce8', border: '1px solid #fde047', borderRadius: '0.5rem', fontSize: 12, color: '#854d0e', lineHeight: 1.4 }}>
                        <strong>Contains estimated costs.</strong> {estimatedCogsCount} item(s) include ingredients
                        priced at their last known purchase price because stock was unavailable and no confirmed
                        closing-stock price exists (₹{estimatedCogsAmount.toFixed(2)} of the total cost). This note is
                        included in the exported file.
                    </div>
                )}

                {/* Exclusion notice (A2). Also shown BEFORE the export: a forwarded PDF whose
                    totals quietly omit two dishes is a trust incident, and the owner should
                    know which dishes those are while they can still go and price them. */}
                {excludedFootnote && (
                    <div style={{ padding: '10px 12px', marginBottom: 14, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '0.5rem', fontSize: 12, color: '#4b5563', lineHeight: 1.4 }}>
                        <strong>Excludes {excludedCogsCount} unpriced item(s).</strong> {excludedCogsNames.length ? `${excludedCogsNames.join(', ')} ` : ''}
                        {excludedCogsCount === 1 ? 'has' : 'have'} no price history at all — never purchased, never counted —
                        so both {excludedCogsCount === 1 ? 'its' : 'their'} revenue (₹{excludedCogsRevenue.toFixed(2)}) and cost are
                        held out of every profit figure here. This note is included in the exported file.
                    </div>
                )}

                {/* Section Selection */}
                <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Select Sections to Export</span>
                        <button onClick={toggleAll} style={{ background: 'none', border: 'none', color: '#4b5563', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                            {allAvailableSelected ? 'Deselect All' : 'Select All'}
                        </button>
                    </div>

                    {/* Summary group header */}
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#000000', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '4px 0 6px 4px' }}>
                        Summary
                    </div>

                    {SECTIONS.filter(s => summaryIds.includes(s.id)).map(section => {
                        const unavailable = !sectionHasData(section.id);
                        const isSelected = !unavailable && selectedSections.includes(section.id);
                        return (
                            <div key={section.id} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '14px 16px', background: unavailable ? '#f9fafb' : (isSelected ? '#fafafa' : '#fff'),
                                border: 'none',
                                boxShadow: isSelected
                                    ? '0 2px 8px rgba(0,0,0,0.10), 0 0 0 1.5px #e2e8f0'
                                    : '0 1px 4px rgba(0,0,0,0.07)',
                                borderRadius: '0.75rem', cursor: unavailable ? 'not-allowed' : 'pointer',
                                opacity: unavailable ? 0.55 : 1, transition: 'all 0.2s', marginBottom: 8
                            }} onClick={() => { if (!unavailable) toggleSection(section.id); }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        readOnly
                                        style={{
                                            width: 22, height: 22, cursor: unavailable ? 'not-allowed' : 'pointer',
                                            accentColor: '#000000', flexShrink: 0, pointerEvents: 'none'
                                        }}
                                    />
                                    <span style={{ fontSize: 15, fontWeight: isSelected ? 600 : 500, color: isSelected ? '#111' : '#4b5563' }}>
                                        {section.label}
                                    </span>
                                </div>
                                <span style={badgeStyle}>{unavailable ? 'No data' : (exportFormat === 'pdf' ? 'Table' : 'Sheet')}</span>
                            </div>
                        );
                    })}

                    {/* Charts & Tables group header */}
                    {chartIds.length > 0 && (
                        <>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#000000', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '12px 0 6px 4px' }}>
                                Charts & Tables
                            </div>
                            {SECTIONS.filter(s => !summaryIds.includes(s.id)).map(section => {
                                const unavailable = !sectionHasData(section.id);
                                const isSelected = !unavailable && selectedSections.includes(section.id);
                                return (
                                    <div key={section.id} style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        padding: '14px 16px', background: unavailable ? '#f9fafb' : (isSelected ? '#fafafa' : '#fff'),
                                        border: 'none',
                                        boxShadow: isSelected
                                            ? '0 2px 8px rgba(0,0,0,0.10), 0 0 0 1.5px #e2e8f0'
                                            : '0 1px 4px rgba(0,0,0,0.07)',
                                        borderRadius: '0.75rem', cursor: unavailable ? 'not-allowed' : 'pointer',
                                        opacity: unavailable ? 0.55 : 1, transition: 'all 0.2s', marginBottom: 8
                                    }} onClick={() => { if (!unavailable) toggleSection(section.id); }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                readOnly
                                                style={{
                                                    width: 22, height: 22, cursor: unavailable ? 'not-allowed' : 'pointer',
                                                    accentColor: '#000000', flexShrink: 0, pointerEvents: 'none'
                                                }}
                                            />
                                            <span style={{ fontSize: 15, fontWeight: isSelected ? 600 : 500, color: isSelected ? '#111' : '#4b5563' }}>
                                                {section.label}
                                            </span>
                                        </div>
                                        <div style={{ fontSize: 11, fontWeight: 600, color: isSelected ? '#374151' : '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            {unavailable ? 'No data' : (exportFormat === 'pdf' ? (section.group === 'table' ? 'Table' : 'Chart + Table') : 'Sheet')}
                                        </div>
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>

                {/* Period Info */}
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '8px 12px', marginBottom: 16,
                    background: '#f5f5f5', borderRadius: '0.5rem',
                    fontSize: 12, color: '#6b7280',
                }}>
                    <span>Period: <strong style={{ color: '#374151' }}>{periodLabel}</strong></span>
                </div>

                {/* Export Button */}
                <button
                    onClick={handleExport}
                    disabled={isExporting || selectedSections.length === 0 || isLoadingData}
                    style={{
                        ...primaryBtnStyle,
                        width: '100%', padding: '14px 24px', fontSize: 15,
                        opacity: (isExporting || selectedSections.length === 0 || isLoadingData) ? 0.6 : 1,
                        cursor: (isExporting || selectedSections.length === 0 || isLoadingData) ? 'not-allowed' : 'pointer',
                    }}
                >
                    {(() => {
                        const fmtName = exportFormat === 'pdf' ? 'PDF' : exportFormat === 'json' ? 'JSON' : 'Excel';
                        if (isLoadingData) return 'Fetching Report Data...';
                        if (isExporting) return `Generating ${fmtName}...`;
                        return `Export as ${fmtName} (${selectedSections.length} section${selectedSections.length !== 1 ? 's' : ''})`;
                    })()}
                </button>
            </div>
        </div>
    );
};

// ─── Styles ─────────────────────────────────────────────
const overlayStyle = {
    position: 'fixed', inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 99999,
    backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
};

const modalStyle = {
    width: 'min(500px, 100%)',
    background: '#fff', borderRadius: '0.5rem',
    boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
    padding: '24px 20px',
    maxHeight: '88vh', overflowY: 'auto',
};

const primaryBtnStyle = {
    padding: '12px 24px',
    backgroundColor: '#000000', color: '#fff',
    border: 'none', borderRadius: '0.5rem',
    fontSize: 14, fontWeight: 700, cursor: 'pointer',
};

const badgeStyle = { fontSize: 10, color: '#9ca3af', whiteSpace: 'nowrap' };

export default ExportReportModal;
